import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import ScreenLayout from '@/components/layout/ScreenLayout'
import DatePicker, { type DatePickerValue } from '@/components/ui/DatePicker'
import QueryState from '@/components/ui/QueryState'
import Skeleton from '@/components/ui/Skeleton'
import TextLink from '@/components/ui/TextLink'
import { failureMessage, portfolioFailure } from '@/features/portfolio/api/errors'
import { useHoldings } from '@/features/portfolio/api/queries'
import BySectorCard from '@/screens/portfolio/components/allocation/BySectorCard'
import ConcentrationCard from '@/screens/portfolio/components/allocation/ConcentrationCard'
import PortfolioFamilyHeader from '@/screens/portfolio/components/PortfolioFamilyHeader'
import { useActiveShareholderId } from '@/screens/portfolio/components/portfolio-switcher/activeProfile'

export const Route = createFileRoute('/_app/portfolio/allocation')({ component: AllocationScreen })

/**
 * Portfolio · Allocation (V2 `4236:76594`, V1 997:27274).
 *
 * Everything here derives from ONE read, `GET /holdings/` — there is no
 * allocation endpoint. The sector split itself is never re-derived: it comes
 * straight from `@/features/portfolio/allocation`'s `sectorWeights`, the one
 * place the "a null weightPercent drops out rather than counting as zero"
 * rule lives. `./components/allocation/deriveAllocation.ts` only adds the
 * figures that module has no reason to carry (a sector's Rs total, its member
 * tickers, the concentration ranking).
 *
 * Company logos and sector icons have no source anywhere in the v5 API — see
 * the delivery report's BACKEND CHANGES NEEDED. Every swatch here is a flat
 * categorical colour, never a fetched image.
 *
 * "Concentration" carries no heading of its own here — V2's one Allocation
 * frame hides the section head behind the card and shows only its in-card
 * eyebrow (`ConcentrationCard`'s own `SectionCard eyebrow="Concentration"`);
 * a second, screen-level `<h3>Concentration</h3>` on top of that eyebrow is
 * the same name twice. "By sector" is unaffected — that module carries no
 * eyebrow of its own, so its section heading is still the only name it has.
 * The page head is the mock's (owner pass 2026-10-29): "Allocation" at the left, the "Allocation on" date button
 * and the gold "Holdings" link at the right. The date reads nothing (no as-of API; the picker's day grid says
 * "Not available yet", asks/pf2.md AL1); `PortfolioFamilyHeader` still keeps the `<h1>` for screen readers.
 *
 * The mock draws no closing Mitra banner on any Portfolio page.
 *
 * ── View state, via `QueryState` ────────────────────────────────────────────
 *
 * `GET /holdings/` answers 200 with `data: []` both for a member who has sold
 * everything and for a member who has never created a shareholder profile, so
 * an empty book renders the empty state, never an error card. `QueryState`
 * (rather than a hand-rolled `isPending`/`isError` ternary) is what keeps a
 * background refetch's failure from swapping the two cards for an error block
 * once they have loaded, and reads a first load with no connection as
 * `offline` rather than a skeleton that never resolves.
 */
function AllocationScreen() {
  const shareholderId = useActiveShareholderId()
  const query = useHoldings(shareholderId)
  // The "Allocation on" day. It reads nothing: `/allocation/` and `/holdings/` answer for today only,
  // so the picker's day grid says "Not available yet" (owner P6, asks/pf2.md AL1).
  const [asOf, setAsOf] = useState<DatePickerValue>({ kind: 'today' })

  return (
    <ScreenLayout
      header={<PortfolioFamilyHeader tab="allocation" compactAction />}
      pageTitle="Allocation"
    >
      <QueryState
        query={query}
        isEmpty={(holdings) => holdings.length === 0}
        skeleton={
          <>
            <Skeleton className="h-72 w-full rounded-lg" />
            <Skeleton className="h-72 w-full rounded-lg" />
          </>
        }
        loadingLabel="Loading your allocation…"
        size="page"
        empty={{
          title: 'Nothing to allocate yet',
          description: 'Add a holding and this tab breaks it down by sector.',
        }}
        failed={{
          title: 'Allocation did not load',
          description: failureMessage(portfolioFailure(query.error) ?? { kind: 'failed' }),
        }}
        className="flex flex-col gap-6"
      >
        {(holdings) => (
          <>
            {/* The mock's page head: the name at the left; the as-of date and the gold Holdings link at the right. */}
            <div className="flex min-h-9 items-center justify-between gap-4">
              <h2 className="text-h-m">Allocation</h2>
              <div className="flex items-center gap-4">
                <DatePicker
                  value={asOf}
                  onChange={setAsOf}
                  cta="Show allocation"
                  title="Allocation on"
                  fieldLabel="Allocation on"
                  range={false}
                  dayAvailable={false}
                />
                <TextLink to="/portfolio/holdings">Holdings</TextLink>
              </div>
            </div>

            <BySectorCard holdings={holdings} />

            <ConcentrationCard holdings={holdings} />
          </>
        )}
      </QueryState>
    </ScreenLayout>
  )
}
